import { Formik, FormikConfig, useFormikContext } from "formik";
import { useContext } from "react";
import { toast } from "react-toastify";
import * as Yup from "yup";
import {
  useCustomerOptions,
  usePatronList,
  useVendorList,
} from "../../api/get";
import { PostCreateCustomer } from "../../api/post";
import { UpdatePatronDetail } from "../../api/put";
import { useToast } from "../../context/ToasterContext/ToasterContext";
import { PopupContext } from "../PopUp/Popup-Context";
import { HandleError } from "../../utils/services";

export interface NewCustomerValue {
  id?: number;
  pan: number | null;
  mobile_no: number | null;
  name: string;
  role_id: number | null | number[] | string;
  additional_info: {
    address: string;
  };
  email: string;
  amount?: number | null;
  bill_due_notice_day: number;
  transaction_column_type?: string;
}

export const initialValues: NewCustomerValue = {
  pan: null,
  mobile_no: null,
  name: "",
  role_id: "",
  additional_info: {
    address: "",
  },
  email: "",
  amount: null,
  bill_due_notice_day: 0,
  transaction_column_type: "dr",
};

const validationSchema = Yup.object().shape({
  pan: Yup.string()
    .length(9, "Must be of 9 digit")
    .when(["role_id"], {
      is: "4",
      then: Yup.string().required("Please enter the pan no").nullable(),
    })
    .nullable(),

  name: Yup.string().required("Please enter customer name"),
  role_id: Yup.string().required("Please select a role"),
  email: Yup.string().email("Must be a valid email"),
  bill_due_notice_day: Yup.number().when(["role_id"], {
    is: "4",
    then: Yup.number().min(0, "Bill due notice days cannot be negative"),
  }),
  additional_info: Yup.object().shape({
    //check regex for address
    address: Yup.string()
      .required("Please enter the address")
      .matches(
        /^[A-Za-z0-9\s,.-]*$/,
        "Please enter a valid address without special characters",
      ),
  }),
  amount: Yup.number()
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .min(0, "Amount cannot be negative"),
});

export const useNewCustomerWrapper = () => {
  const formik = useFormikContext<NewCustomerValue>();
  if (!formik) {
    throw new Error("useNewCustomerWrapper must be used within a Formik");
  }
  return formik;
};
interface NewCustomeWrapperProps {
  children: React.ReactNode;
  initialValueProps?: { id: number; name: string };
}

export const NewCustomeWrapper: React.FC<NewCustomeWrapperProps> = ({
  children,
  initialValueProps,
}) => {
  const { closeModal } = useContext(PopupContext);
  const { refetch } = usePatronList();
  const { refetch: customerOptionRefetch } = useCustomerOptions();
  const { refetch: vendorRefetch } = useVendorList();
  const { showSuccessMessage, showErrorMessage } = useToast();

  const handleSubmit: FormikConfig<NewCustomerValue>["onSubmit"] = (
    values,
    { setSubmitting },
  ) => {
    setSubmitting(true);
    const data = {
      ...values,
    };
    const newRoleArr = [];
    newRoleArr.push(Number(values.role_id));
    data.role_id = newRoleArr as number[];
    data.pan = Number(values.pan) > 0 ? Number(values.pan) : null;
    data.bill_due_notice_day = Number(values.bill_due_notice_day);
    data.amount =values.amount == null
        ? null
        : Number(values.amount);

    if (initialValueProps) {
      //Update data
      UpdatePatronDetail(initialValueProps.id as number, data)
        .then((res) => {
          showSuccessMessage("User data updated successfully");
          setSubmitting(false);
          closeModal();
          refetch();
          customerOptionRefetch();
          vendorRefetch();
        })
        .catch((err: any) => {
          const errorMessage = HandleError(err);
          showErrorMessage(errorMessage);
        })
        .finally(() => {
          setSubmitting(false);
        });
      return;
    }

    PostCreateCustomer(data)
      .then((res) => {
        toast.success("New patron added successfully");
        setSubmitting(false);
        closeModal();
        refetch();
        customerOptionRefetch();
        vendorRefetch();
      })
      .catch((err: any) => {
        let errorMessage = "Failed to submit new customer";
        if (err.message) {
          errorMessage = err.message;
        }
        if (err.response.data.status.status_code === 422) {
          errorMessage = err?.response?.data?.status?.status_message[0].msg;
        } else if (err.response.data.status.status_message) {
          errorMessage = err?.response?.data?.status?.status_message;
        }
        toast.error(errorMessage);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {children}
    </Formik>
  );
};
